/**
 * OWL2 Prefix Manager
 *
 * Manages namespace prefix declarations for IRI expansion and abbreviation.
 */

import type { IRI, AbbreviatedIRI, FullIRI } from '../types/IRI.ts';
import { fullIRI, abbreviatedIRI } from '../types/IRI.ts';
import { DEFAULT_PREFIXES } from './constants.ts';

/**
 * Manages prefix-to-namespace mappings for IRI expansion and abbreviation.
 */
export class PrefixManager {
    private prefixes: Map<string, string> = new Map();
    private reverseMap: Map<string, string> = new Map();

    /**
     * Create a new PrefixManager, optionally with standard prefixes pre-registered.
     */
    constructor(includeDefaults: boolean = false) {
        if (includeDefaults) {
            for (const [prefix, namespace] of Object.entries(DEFAULT_PREFIXES)) {
                this.registerPrefix(prefix, namespace);
            }
        }
    }

    /**
     * Register a prefix mapping.
     */
    registerPrefix(name: string, namespace: string): void {
        this.prefixes.set(name, namespace);
        // Only update reverse map if this namespace isn't already mapped
        // (prefer shorter prefixes for abbreviation)
        if (!this.reverseMap.has(namespace)) {
            this.reverseMap.set(namespace, name);
        }
    }

    /**
     * Remove a prefix mapping.
     */
    removePrefix(name: string): boolean {
        const namespace = this.prefixes.get(name);
        if (namespace) {
            this.prefixes.delete(name);
            // Only remove from reverse map if this was the mapped prefix
            if (this.reverseMap.get(namespace) === name) {
                this.reverseMap.delete(namespace);
            }
            return true;
        }
        return false;
    }

    /**
     * Get the namespace for a prefix.
     */
    getNamespace(prefix: string): string | undefined {
        return this.prefixes.get(prefix);
    }

    /**
     * Get the preferred prefix for a namespace.
     */
    getPrefix(namespace: string): string | undefined {
        return this.reverseMap.get(namespace);
    }

    /**
     * Check if a prefix is registered.
     */
    hasPrefix(prefix: string): boolean {
        return this.prefixes.has(prefix);
    }

    /**
     * Get all registered prefixes.
     */
    getAllPrefixes(): Array<{ name: string; namespace: string }> {
        return Array.from(this.prefixes.entries()).map(([name, namespace]) => ({
            name,
            namespace
        }));
    }

    /**
     * Expand an abbreviated IRI to a full IRI.
     * Returns undefined if the prefix is not registered.
     */
    expand(abbreviated: AbbreviatedIRI): FullIRI | undefined {
        const namespace = this.prefixes.get(abbreviated.prefix);
        if (!namespace) return undefined;
        return fullIRI(namespace + abbreviated.localName);
    }

    /**
     * Expand a prefixed string (e.g., "owl:Thing") to a full IRI string.
     * Returns undefined if the prefix is not registered.
     */
    expandString(prefixedIRI: string): string | undefined {
        const colonIndex = prefixedIRI.indexOf(':');
        if (colonIndex === -1) return undefined;

        const prefix = prefixedIRI.substring(0, colonIndex);
        const localName = prefixedIRI.substring(colonIndex + 1);

        const namespace = this.prefixes.get(prefix);
        if (!namespace) return undefined;

        return namespace + localName;
    }

    /**
     * Abbreviate a full IRI to an abbreviated IRI using registered prefixes.
     * Returns undefined if no matching prefix is found.
     */
    abbreviate(full: FullIRI): AbbreviatedIRI | undefined {
        // Find the longest matching namespace
        let matchedNamespace: string | undefined;
        let matchedPrefix: string | undefined;

        for (const [namespace, prefix] of this.reverseMap) {
            if (full.value.startsWith(namespace)) {
                if (!matchedNamespace || namespace.length > matchedNamespace.length) {
                    matchedNamespace = namespace;
                    matchedPrefix = prefix;
                }
            }
        }

        if (matchedNamespace && matchedPrefix !== undefined) {
            const localName = full.value.substring(matchedNamespace.length);
            return abbreviatedIRI(matchedPrefix, localName);
        }

        return undefined;
    }

    /**
     * Abbreviate a full IRI string to a prefixed string (e.g., "owl:Thing").
     * Returns undefined if no matching prefix is found.
     */
    abbreviateString(fullIRI: string): string | undefined {
        // Find the longest matching namespace
        let matchedNamespace: string | undefined;
        let matchedPrefix: string | undefined;

        for (const [namespace, prefix] of this.reverseMap) {
            if (fullIRI.startsWith(namespace)) {
                if (!matchedNamespace || namespace.length > matchedNamespace.length) {
                    matchedNamespace = namespace;
                    matchedPrefix = prefix;
                }
            }
        }

        if (matchedNamespace && matchedPrefix !== undefined) {
            const localName = fullIRI.substring(matchedNamespace.length);
            return matchedPrefix ? `${matchedPrefix}:${localName}` : localName;
        }

        return undefined;
    }

    /**
     * Resolve an IRI to its full string form.
     * - If it's a FullIRI, returns the value.
     * - If it's an AbbreviatedIRI, expands it using registered prefixes.
     */
    resolve(iri: IRI): string | undefined {
        if (iri.kind === 'FullIRI') {
            return iri.value;
        }
        return this.expandString(`${iri.prefix}:${iri.localName}`);
    }

    /**
     * Clone this PrefixManager.
     */
    clone(): PrefixManager {
        const pm = new PrefixManager();
        for (const [name, namespace] of this.prefixes) {
            pm.registerPrefix(name, namespace);
        }
        return pm;
    }
}

/**
 * Create a PrefixManager with standard OWL/RDF prefixes.
 */
export function createStandardPrefixManager(): PrefixManager {
    return new PrefixManager(true);
}
